import {TabProps, Tabs} from 'platform/components';
import {Show} from 'platform/foundation';

import {not} from 'ramda';

import {GetServiceOrderReturnResponse} from '@dms/api/warehouse';
import {useRefreshDocumentsCount} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {queryParams, Section, usePermissions} from '@dms/shared';
import {ServiceOrderReturnMaterialModal} from '@dms/shared-aftersales';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {Nullish, RequiredTestIdProps, suffixTestId, useBoolean, useQueryState} from 'shared/web';

import {GeneralInformationTab} from './components/GeneralInformationTab';
import {ListOfItemsTab} from './components/ListOfItemsTab';

interface OverviewProps extends RequiredTestIdProps {
  serviceOrderReturn: GetServiceOrderReturnResponse | Nullish;
  isServiceOrderReturnLoading: boolean;
}

export function Overview(props: OverviewProps) {
  const {serviceOrderReturnId} = useWarehouseParams();

  const refreshDocumentsCount = useRefreshDocumentsCount({
    contextTarget: 'service-order-return',
    contextId: serviceOrderReturnId,
  });

  const [activeTabId, setActiveTabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);

  const [canCreateReturnToWarehouse] = usePermissions({
    permissionKeys: ['returnToWarehouseCreate'],
  });

  const [isReturnMaterialModalVisible, openReturnMaterialModal, closeReturnMaterialModal] =
    useBoolean(false);

  const isServiceOrderReturnCompleted =
    props.serviceOrderReturn?.serviceOrderReturn?.state === 'COMPLETED';

  const tabs: TabProps[] = [
    {
      id: 'general-information',
      title: i18n.t('general.labels.generalInformation'),
      content: (
        <GeneralInformationTab
          serviceOrderReturn={props.serviceOrderReturn}
          isServiceOrderReturnLoading={props.isServiceOrderReturnLoading}
          data-testid={suffixTestId('tabs.generalInformation', props)}
        />
      ),
    },
    {
      id: 'list-of-items',
      title: i18n.t('general.labels.listOfItems'),
      actions: [
        {
          isDisabled: not(canCreateReturnToWarehouse) || isServiceOrderReturnCompleted,
          type: 'button',
          variant: 'outlined',
          title: i18n.t('general.actions.addMaterial'),
          onClick: openReturnMaterialModal,
          'data-testid': suffixTestId('actions.add', props),
        },
      ],
      content: (
        <ListOfItemsTab
          totalPrice={props.serviceOrderReturn?.sparePartsTotalPrice?.totalPurchasePrice}
          data-testid={suffixTestId('tabs.listOfItems', props)}
        />
      ),
    },
  ];

  const handleSubmit = () => {
    refreshDocumentsCount();
    closeReturnMaterialModal();
  };

  return (
    <Section isFullHeight data-testid={suffixTestId('wrapper', props)}>
      <Tabs
        isFullHeight
        isLazy
        variant="condensed"
        tabs={tabs}
        activeTabId={activeTabId}
        onChange={setActiveTabId}
      />
      <Show when={isReturnMaterialModalVisible}>
        <ServiceOrderReturnMaterialModal
          serviceOrderReturnId={serviceOrderReturnId}
          authorizationProfileId={props.serviceOrderReturn?.serviceOrder?.authorizationProfileId}
          onSubmit={handleSubmit}
          data-testid={suffixTestId('dialogs.returnMaterial', props)}
        />
      </Show>
    </Section>
  );
}
